import React, { Fragment, useCallback } from "react";
import { Button, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { CatalogOffering, PartnerCatalog } from "../../data/catalog";
import { useMediaQuery, useQueryString } from "../../hooks";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { usePartnerCatalogsQuery } from "../../offering/api";
import { Spacer } from "../../offering/spacer";
import { useAnalytics } from "../../lib/analytics/analytics";
import { useGetLightPartnerOffering } from "../../data/api";
import { COMING_FROM_CATALOG } from "../hooks/use_handle_press_back";

interface MoreToExploreProps {
  exploreMoreHeader?: string;
  partnerSlug: string;
  currentPartnerOffering: string;
  fromCatalog?: string;
  categoryOfferings?: string[];
}

interface MoreToExploreMembershipProps {
  partnerSlug: string;
  currentPartnerOffering: string;
}

const getCatalogOfferings = (
  pc?: PartnerCatalog,
  { membership = false } = {}
) => {
  if (!pc) {
    return null;
  }

  const offerings = pc.partnerCatalogCategories.flatMap(
    (category) => category.catalogOfferings ?? []
  );
  if (membership) {
    return offerings.filter((offering) => !offering.membership);
  }
  return offerings;
};

export function MoreToExplore(props: MoreToExploreProps) {
  const {
    exploreMoreHeader,
    partnerSlug,
    currentPartnerOffering,
    fromCatalog,
    categoryOfferings,
  } = props;
  const partnerCatalogs = usePartnerCatalogsQuery(partnerSlug);
  const partnerCatalog = fromCatalog
    ? partnerCatalogs.find((pc) => pc.slug === fromCatalog)
    : partnerCatalogs.find((pc) => !pc.slug);
  const catalogOfferings = getCatalogOfferings(partnerCatalog);
  const { fbt } = useI18n();

  if (!catalogOfferings) {
    return null;
  }

  return (
    <>
      {partnerCatalog && catalogOfferings.length > 1 && (
        <div data-testid="more-to-explore" className="more-to-explore">
          {exploreMoreHeader ? (
            <div
              className="explore-more-header"
              dangerouslySetInnerHTML={{
                __html: exploreMoreHeader,
              }}
            />
          ) : (
            <h3>{fbt("More to explore...")}</h3>
          )}
          <Spacer size={24} />
          <ExploreCategory
            catalogOfferings={catalogOfferings}
            catalogSlug={fromCatalog}
            currentPartnerOffering={currentPartnerOffering}
            categoryOfferings={categoryOfferings}
          />
        </div>
      )}
    </>
  );
}

export function MoreToExploreMembership(props: MoreToExploreMembershipProps) {
  const { partnerSlug, currentPartnerOffering } = props;
  const { fromCatalog } = useQueryString();
  const partnerCatalogs = usePartnerCatalogsQuery(partnerSlug);
  const partnerCatalog =
    partnerCatalogs.find((c) => c.slug === fromCatalog) || partnerCatalogs[0];
  const catalogOfferings = getCatalogOfferings(partnerCatalog, {
    membership: true,
  });
  const { fbt } = useI18n();

  if (!catalogOfferings) {
    return null;
  }

  return (
    <>
      {partnerCatalog && catalogOfferings.length > 1 && (
        <div className="more-to-explore">
          <h3>
            {fbt("Not ready for a membership? Try our workspaces for a day")}
          </h3>
          <Spacer size={24} />
          <ExploreCategory
            catalogOfferings={catalogOfferings}
            catalogSlug={undefined}
            currentPartnerOffering={currentPartnerOffering}
          />
        </div>
      )}
    </>
  );
}

interface ExploreCategoryProps {
  catalogOfferings: CatalogOffering[];
  catalogSlug?: string;
  currentPartnerOffering: string;
  categoryOfferings?: string[];
}

function ExploreCategory(props: ExploreCategoryProps) {
  const {
    catalogOfferings,
    catalogSlug,
    currentPartnerOffering,
    categoryOfferings,
  } = props;
  const { device } = useMediaQuery();
  const { locale } = useI18n();
  const filteredCatalogOfferings = catalogOfferings.filter(
    (co) =>
      (currentPartnerOffering && co.slug !== currentPartnerOffering) ||
      (!!categoryOfferings && !categoryOfferings?.includes(co.slug))
  );
  const slidesCount = Math.min(
    filteredCatalogOfferings.length,
    ["mobile", "tablet", "desktop"].indexOf(device) + 1
  );

  return (
    <div className="row gx-0 gx-sm-4">
      <Slider
        slidesToShow={slidesCount}
        arrows={device !== "mobile"}
        dots
        centerPadding="10"
        rtl={locale.direction === "rtl"}
      >
        {filteredCatalogOfferings.map((catalogOffering, index) => (
          <Fragment key={catalogOffering.id}>
            {!catalogOffering.targetUrl ? (
              <CatalogOfferingCardV2
                key={index}
                image={catalogOffering.image}
                title={catalogOffering.title}
                description={catalogOffering.description}
                offeringSlug={catalogOffering.slug}
                catalogSlug={catalogSlug}
                comingSoon={catalogOffering.comingSoon}
                membership={catalogOffering.membership}
              />
            ) : (
              <CatalogOfferingTargetUrlCardV2
                key={index}
                image={catalogOffering.image}
                title={catalogOffering.title}
                description={catalogOffering.description}
                comingSoon={catalogOffering.comingSoon}
                targetUrl={catalogOffering.targetUrl}
                membership={catalogOffering.membership}
              />
            )}
          </Fragment>
        ))}
      </Slider>
    </div>
  );
}

export interface CatalogOfferingCardProps {
  image: string;
  title: string;
  description: string;
  offeringSlug: string;
  catalogSlug?: string;
  comingSoon: boolean;
  membership?: boolean;
  fromCategory?: string;
  ctaLabel?: string;
}

export function CatalogOfferingCardV2(props: CatalogOfferingCardProps) {
  const {
    image,
    title,
    description,
    offeringSlug,
    catalogSlug,
    comingSoon,
    membership,
    fromCategory,
    ctaLabel,
  } = props;
  const { fbt } = useI18n();
  const analytics = useAnalytics();
  const partnerOffering = useGetLightPartnerOffering(offeringSlug);

  const searchParams = new URLSearchParams(window.location.search);

  if (fromCategory) {
    searchParams.set("fromCategory", fromCategory);
  }

  if (catalogSlug) {
    searchParams.set("fromCatalog", catalogSlug);
  }

  const handleClick = useCallback(() => {
    analytics.event(
      `Catalog Offering Card Click`,
      {
        Template: partnerOffering.template,
        Title: title,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, title, partnerOffering.template]);

  return (
    <Link
      // @ts-expect-error: this is accepted by react-router
      to={
        comingSoon
          ? null
          : {
              pathname: `/offerings/${offeringSlug}`,
              search: searchParams.toString(),
              state: { comingFrom: COMING_FROM_CATALOG },
            }
      }
      style={{
        textDecoration: "none",
        cursor: comingSoon ? "auto" : "pointer",
        height: "100%",
      }}
      onClick={comingSoon ? () => {} : handleClick}
    >
      <Card bg="light" className="h-100">
        <Card.Img
          variant="top"
          src={image}
          title={title}
          style={{
            objectFit: "cover",
            width: "100%",
            height: 193,
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
          }}
        />
        <Card.Body className="d-flex flex-column justify-content-between gap-3 more-to-explore-card-body">
          <div>
            <Card.Title className="fw-bold fs-6 more-to-explore-card-title">
              {title}
            </Card.Title>
            <Card.Text className="more-to-explore-card-description">
              {description}
            </Card.Text>
          </div>
          <Button
            style={{ width: "100%" }}
            variant="outline-primary"
            disabled={comingSoon}
          >
            {comingSoon
              ? fbt("Coming Soon")
              : ctaLabel
              ? ctaLabel
              : membership
              ? fbt("Subscribe now")
              : fbt("Book now")}
          </Button>
        </Card.Body>
      </Card>
    </Link>
  );
}

export interface CatalogOfferingTargetUrlCardProps {
  image: string;
  title: string;
  description: string;
  comingSoon: boolean;
  membership?: boolean;
  targetUrl: string;
  ctaLabel?: string;
}

export function CatalogOfferingTargetUrlCardV2(
  props: CatalogOfferingTargetUrlCardProps
) {
  const {
    image,
    title,
    description,
    comingSoon,
    membership,
    targetUrl,
    ctaLabel,
  } = props;
  const { fbt } = useI18n();

  return (
    <Link to={targetUrl} style={{ textDecoration: "none" }}>
      <Card bg="light" text="dark" className="h-100">
        <Card.Img
          variant="top"
          src={image}
          title={title}
          style={{
            objectFit: "cover",
            width: "100%",
            height: 193,
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
          }}
        />
        <Card.Body className="d-flex flex-column justify-content-between gap-3">
          <div>
            <Card.Title className="fw-bold fs-6">{title}</Card.Title>
            <Card.Text className="more-to-explore-card-description">
              {description}
            </Card.Text>
          </div>
          <Button
            style={{ width: "100%" }}
            variant="outline-primary"
            disabled={comingSoon}
          >
            {comingSoon
              ? fbt("Coming Soon")
              : ctaLabel
              ? ctaLabel
              : membership
              ? fbt("Subscribe now")
              : fbt("Book now")}
          </Button>
        </Card.Body>
      </Card>
    </Link>
  );
}
